import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { APPLICATION_API_BASE_URL } from '../configuration/config';

const axiosInstance = axios.create({
  baseURL: APPLICATION_API_BASE_URL
});

axiosInstance.interceptors.request.use((config) => {
  const sessionId = Cookies.get('sessionId');
  if (sessionId) {
    config.headers = config.headers || {};
    config.headers['x-session-id'] = sessionId;
  }
  return config;
});

// Add a response interceptor to handle 401 errors
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      const message = error.response?.data?.message || '';
      const original = error.config || {};

      // Revoked/orphan session: drop sessionId once and retry with JWT only
      if (
        !original._sessionRetry &&
        typeof message === 'string' &&
        message.toLowerCase().includes('session has been logged out')
      ) {
        Cookies.remove('sessionId');
        original._sessionRetry = true;
        if (original.headers) {
          delete original.headers['x-session-id'];
        }
        return axiosInstance(original);
      }

      const refreshToken = Cookies.get('refreshToken');
      if (refreshToken && !original._retry) {
        original._retry = true;
        try {
          const sessionId = Cookies.get('sessionId');
          const refreshHeaders = {};
          if (sessionId) {
            refreshHeaders['x-session-id'] = sessionId;
          }
          // Call your newToken endpoint
          const refreshResponse = await axios.post(
            `${APPLICATION_API_BASE_URL}/auth/newToken`,
            {
              refresh_token: refreshToken,
              token: Cookies.get('token')
            },
            { headers: refreshHeaders }
          );
          const newToken = refreshResponse.data.data.id_token;
          const newRefreshToken = refreshResponse.data?.data?.refresh_token;
          const newSessionId = refreshResponse.data?.data?.sessionId;
          Cookies.set('token', newToken);
          if (newRefreshToken) {
            Cookies.set('refreshToken', newRefreshToken);
          }
          if (newSessionId) {
            Cookies.set('sessionId', newSessionId);
          }
          // Update the original request with the new token and retry
          original.headers = original.headers || {};
          original.headers['authorization'] = `${newToken}`;
          if (newSessionId) {
            original.headers['x-session-id'] = newSessionId;
          } else if (original.headers['x-session-id'] && !Cookies.get('sessionId')) {
            delete original.headers['x-session-id'];
          }
          return axiosInstance(original);
        } catch (refreshError) {
          // Refresh failed, log out
          toast.error('Session expired. Please login again.');
          Object.keys(Cookies.get()).forEach((cookie) => {
            Cookies.remove(cookie);
          });
          window.location.replace('/');
        }
      } else if (!refreshToken) {
        toast.error('Session expired. Please login again.');
        Object.keys(Cookies.get()).forEach((cookie) => {
          Cookies.remove(cookie);
        });
        window.location.replace('/');
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
